import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type IntegridadeStatus = 'ok' | 'warning' | 'critical';

export interface IntegridadeFinanceira {
  coberturaFinanceira: number;
  valorNaoVinculado: number;
  divergenciaBac: number;
  itensSemVinculo: number;
  itensCriticos: number;
  status: IntegridadeStatus;
  hasData: boolean;
}

const EMPTY: IntegridadeFinanceira = {
  coberturaFinanceira: 0,
  valorNaoVinculado: 0,
  divergenciaBac: 0,
  itensSemVinculo: 0,
  itensCriticos: 0,
  status: 'ok',
  hasData: false,
};

function deriveStatus(
  cobertura: number,
  divergencia: number,
  itensCriticos: number,
  itensSemVinculo: number,
): IntegridadeStatus {
  if (cobertura < 80 || divergencia > 2 || itensCriticos > 0) return 'critical';
  if (cobertura < 95 || itensSemVinculo > 0) return 'warning';
  return 'ok';
}

/**
 * Shared hook for financial integrity indicators.
 * Requires a versaoId (active schedule version) to call the gate RPC.
 * If versaoId is null, returns hasData: false.
 */
export function useIntegridadeFinanceira(versaoId: string | null, contratoId?: string) {
  const query = useQuery({
    queryKey: ['integridade-financeira', versaoId],
    queryFn: async () => {
      const params: Record<string, string> = { p_versao_id: versaoId! };
      if (contratoId) params.p_contrato_id = contratoId;

      const { data, error } = await supabase.rpc('emp_validar_gates_ativacao', params as any);
      if (error) throw error;
      return data as unknown as {
        bloqueado: boolean;
        motivos: string[];
        gates: {
          cobertura_financeira: number;
          cobertura_itens: number;
          valor_total_planilha: number;
          valor_total_vinculado: number;
          valor_nao_vinculado: number;
          bac_calculado: number;
          divergencia_bac: number;
          itens_criticos_sem_vinculo: Array<{ codigo_item: string; valor: number; percentual: number }>;
          total_folhas: number;
          vinculadas: number;
        };
      };
    },
    enabled: !!versaoId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const result = useMemo<IntegridadeFinanceira>(() => {
    if (!query.data?.gates) return EMPTY;

    const g = query.data.gates;
    const itensSemVinculo = g.total_folhas - g.vinculadas;
    const itensCriticos = Array.isArray(g.itens_criticos_sem_vinculo) ? g.itens_criticos_sem_vinculo.length : 0;

    return {
      coberturaFinanceira: g.cobertura_financeira,
      valorNaoVinculado: g.valor_nao_vinculado,
      divergenciaBac: g.divergencia_bac,
      itensSemVinculo,
      itensCriticos,
      status: deriveStatus(g.cobertura_financeira, g.divergencia_bac, itensCriticos, itensSemVinculo),
      hasData: true,
    };
  }, [query.data]);

  return { ...result, isLoading: query.isLoading };
}
